import { useQueryState } from 'nuqs';
import { useOptimisticSearchParams } from 'nuqs/adapters/react-router/v7';
import { useRef } from 'react';
import { useLocation } from 'react-router-dom';

const SERVER_LOCATION = {
  pathname: '/',
};

function useSearchParamsWithLocation(usp: URLSearchParams) {
  const lastUsp = useRef(usp);
  const windowLocation =
    typeof location === 'undefined' ? SERVER_LOCATION : location;
  const lastLocation = useRef(windowLocation);

  if (usp.toString() !== lastUsp.current.toString()) {
    lastUsp.current = usp;
    lastLocation.current = windowLocation;
  }

  return [usp, lastLocation.current] as const;
}

function noop() {
  // no-op function for when navigation is in progress
}

/**
 * Fixed version of useQueryState that prevents refetches when navigating away
 * Based on: https://github.com/47ng/nuqs/discussions/893
 */
export function useQueryStateFixed<TValue = any, TDefault = TValue>(
  key: string,
  parser: any,
  options?: any,
): [
  TValue | TDefault | null,
  (value: any, options?: any) => Promise<URLSearchParams>,
] {
  const location = useLocation();
  const [, optimisticSearchParamsLocation] = useSearchParamsWithLocation(
    useOptimisticSearchParams(),
  );

  const [state, setState] = (useQueryState as any)(key, parser, options);

  const lastValidState = useRef<any>(state);

  const nuqsIsTooFast =
    location.pathname !== optimisticSearchParamsLocation.pathname;

  if (nuqsIsTooFast) {
    // we have to pass noop, because if the location is out of date
    // then setting the state will be for the old location
    return [lastValidState.current, noop as any] as const;
  }

  lastValidState.current = state;

  return [state, setState];
}
